import React from 'react'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import { NumericFormat } from 'react-number-format'

/**
 * Custom component for react-number-format integration with MUI TextField
 */
const NumericFormatCustom = React.forwardRef(function NumericFormatCustom(props, ref) {
    const { onChange, decimalScale = 2, thousandSeparator = true, ...other } = props

    return (
        <NumericFormat
            {...other}
            getInputRef={ref}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: props.name,
                        value: values.floatValue ?? 0,
                    },
                })
            }}
            thousandSeparator={thousandSeparator}
            decimalScale={decimalScale}
            fixedDecimalScale={decimalScale > 0}
            valueIsNumericString
        />
    )
})

/**
 * Reusable Number Input component with consistent styling
 *
 * @param {string} label - Input label
 * @param {number} value - Current numeric value
 * @param {function} onChange - Change handler (receives numeric value)
 * @param {string} name - Optional name attribute
 * @param {boolean} disabled - Whether the input is disabled
 * @param {boolean} required - Whether the field is required
 * @param {string} error - Error message to display
 * @param {string} helperText - Helper text to display below
 * @param {string} prefix - Prefix text/symbol (e.g., "£")
 * @param {string} suffix - Suffix text/symbol (e.g., "kWh")
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @param {number} step - Step value for increment/decrement
 * @param {number} decimalScale - Number of decimal places
 * @param {boolean} thousandSeparator - Whether to show thousand separator
 * @param {boolean} fullWidth - Whether to use full width
 * @param {object} sx - Additional MUI sx styles
 */
const NumberInput = ({
    label,
    value,
    onChange,
    name,
    disabled = false,
    required = false,
    error = '',
    helperText = '',
    prefix = '',
    suffix = '',
    min,
    max,
    step = 1,
    decimalScale = 0,
    thousandSeparator = true,
    fullWidth = false,
    sx = {},
    ...props
}) => {
    const handleChange = (event) => {
        let newValue = event.target.value

        // Apply min/max constraints
        if (min !== undefined && newValue < min) newValue = min
        if (max !== undefined && newValue > max) newValue = max

        if (onChange) {
            onChange(newValue, event)
        }
    }

    const hasError = Boolean(error)

    return (
        <TextField
            label={label}
            name={name}
            value={value ?? 0}
            onChange={handleChange}
            disabled={disabled}
            required={required}
            error={hasError}
            helperText={error || helperText}
            fullWidth={fullWidth}
            size="small"
            sx={{ minWidth: 100, ...sx }}
            InputProps={{
                inputComponent: NumericFormatCustom,
                inputProps: {
                    decimalScale,
                    thousandSeparator,
                    min,
                    max,
                    step,
                },
                startAdornment: prefix ? (
                    <InputAdornment position="start">{prefix}</InputAdornment>
                ) : null,
                endAdornment: suffix ? (
                    <InputAdornment position="end">{suffix}</InputAdornment>
                ) : null,
            }}
            {...props}
        />
    )
}

export default NumberInput
